// ============================================================
// DESIGN TOKENS — fonte única de verdade de cores e tipografia
// Altere AQUI e tudo acompanha: tema MUI (theme.ts), variáveis
// CSS (aplicadas em index.js) e componentes que importam tokens.
// Não usar cores hexadecimais soltas fora deste arquivo.
// ============================================================

export const colors = {
  // Marca
  primary: "#394382", // azul do clube — botões, links, sidebar
  primaryDark: "#182257", // navy do logotipo — hover/ênfase
  primaryHover: "#535B8D", // tom intermediário (hovers na sidebar)
  action: "#EC0000", // vermelho de destaque — use via color="secondary"
  actionSoft: "#FDEBEB", // fundo suave para botões de ação destrutiva

  // Neutras
  white: "#FFFFFF",
  textOnPrimary: "#FFFFFF", // texto sobre fundos primários
  sidebarText: "#E9E9E9", // itens do menu lateral
  sidebarActiveBg: "rgba(255, 255, 255, 0.14)", // fundo do item de menu ativo
  sidebarHoverBg: "rgba(255, 255, 255, 0.07)", // fundo do item de menu em hover
  border: "#E0E0E0", // bordas discretas
  bgSubtle: "#FCFCFC", // fundos de blocos/cartões discretos
  bgContent: "#F2F2FF", // fundo da área de conteúdo do painel
  bgToolbar: "#F2F2F2F9", // toolbar das listagens (com transparência)
  neutral50: "#F9F9F9",
  neutral100: "#F5F5F5",
  neutral200: "#EAEAEA",
  neutral300: "#CCCCCC",
  textMuted: "#6B7280", // textos secundários sobre fundo claro (navbar, legendas)
  textSecondary: "#4B5563", // dados de apoio que precisam ser lidos (metadados de lista)
  textStrong: "#1F2430", // texto principal sobre fundo claro (avisos, toasts)
  black: "#000000",

  // Feedback — avisos, toasts e estados de formulário
  success: "#127C4A", // confirmação (salvo, criado, vinculado)
  warning: "#B26B00", // atenção (operação parcial, pendência)
  info: "#394382", // informativo — segue o azul da marca

  // Marcas de terceiros
  pix: "#32BCAD", // verde oficial do Pix (Banco Central) — badge de forma de pagamento
};

// Raios de borda padronizados (px)
export const radius = {
  sm: 8, // botões, campos, chips, itens de lista
  md: 12, // popovers, flyouts, blocos internos
  lg: 16, // cartões e containers grandes
};

// Sombras padronizadas
export const shadows = {
  card: "0 2px 4px rgba(0, 0, 0, 0.1)",
  popover: "0 6px 24px rgba(0, 0, 0, 0.14)", // menus e dropdowns flutuantes
};

// Dimensões de layout
export const layout = {
  sidebarWidth: 320, // sidebar aberta
  sidebarClosedWidth: 64, // sidebar recolhida — mostra só os ícones
  contentPadding: 20, // respiro horizontal da área de conteúdo (#main-content)
  contentPaddingY: 10, // respiro vertical da área de conteúdo
};

// Paleta dos gráficos do dashboard (mesmas cores já em uso)
export const chartColors = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#33CC33",
  "#FF9900",
  "#CCCCCC",
  "#000000",
];

export const fontFamily =
  "'Inter', -apple-system, 'Segoe UI', Roboto, Arial, sans-serif";

export const fontFamilyMono =
  "ui-monospace, 'Cascadia Code', Consolas, Menlo, monospace"; // IDs técnicos, códigos

// Espelha os tokens como variáveis CSS (--color-*) para uso nos .css.
// Chamado uma vez em index.js, antes do render.
export function applyCssVariables() {
  const root = document.documentElement;
  const vars: Record<string, string> = {
    "--color-primary": colors.primary,
    "--color-primary-dark": colors.primaryDark,
    "--color-primary-hover": colors.primaryHover,
    "--color-action": colors.action,
    "--color-text-on-primary": colors.textOnPrimary,
    "--color-sidebar-text": colors.sidebarText,
    "--color-sidebar-active-bg": colors.sidebarActiveBg,
    "--color-sidebar-hover-bg": colors.sidebarHoverBg,
    "--color-border": colors.border,
    "--color-bg-subtle": colors.bgSubtle,
    "--color-bg-content": colors.bgContent,
    "--color-bg-toolbar": colors.bgToolbar,
    "--color-neutral-50": colors.neutral50,
    "--color-neutral-100": colors.neutral100,
    "--color-neutral-200": colors.neutral200,
    "--font-family": fontFamily,
    // shorthand vertical/horizontal usado pelo #main-content; os eixos
    // separados existem para cálculos (ex.: full-bleed da tela da API)
    "--content-padding": `${layout.contentPaddingY}px ${layout.contentPadding}px`,
    "--content-padding-x": `${layout.contentPadding}px`,
    "--content-padding-y": `${layout.contentPaddingY}px`,
    "--shadow-popover": shadows.popover,
    "--radius-sm": `${radius.sm}px`,
    "--radius-md": `${radius.md}px`,
    "--radius-lg": `${radius.lg}px`,
  };
  Object.entries(vars).forEach(([key, value]) =>
    root.style.setProperty(key, value)
  );
}
